import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, checkResourceLimits } from '../../context/AuthContext';
import logger from '../../utils/logger';
import './Agents.css';
import { personaTemplates, getPersonaPlaceholder } from './agentTemplates';
import LimitWarning from '../../components/common/LimitWarning';

const EditAgent = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { session } = useContext(AuthContext);
  const token = session?.access_token;
  const API_URL = process.env.REACT_APP_API_URL || '';
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('settings');

  // Editable fields
  const [agentName, setAgentName] = useState('');
  const [initialAgentName, setInitialAgentName] = useState('');
  const [agentPersona, setAgentPersona] = useState('');
  const [agentCapability, setAgentCapability] = useState('optimal-choice');
  
  // Added state for agent pages/files
  const [agentFiles, setAgentFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [fileDeleting, setFileDeleting] = useState(null);
  
  // File upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [isOcrEnabled, setIsOcrEnabled] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Constants for file validation
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes

  // Add these state variables inside the component
  const [limitCheck, setLimitCheck] = useState(null);
  const [checkingLimits, setCheckingLimits] = useState(false);

  useEffect(() => {
    // Inline fetch function to avoid dependency warnings
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/agent/${agentId}/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch agent data');
        }
        const data = await response.json();
        setAgentName(data.data.project_name || '');
        setInitialAgentName(data.data.project_name || '');
        setAgentPersona(data.data.persona_instructions || '');
        setAgentCapability(data.data.agent_capability || 'optimal-choice');
      } catch (error) {
        logger.error('Error fetching agent data:', error);
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [agentId, API_URL, token]);

  const fetchAgentFiles = async () => {
      setFilesLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/agent/${agentId}/pages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch agent files');
        }
        const { data } = await response.json();
        setAgentFiles(data || []);
      } catch (error) {
        console.error('Error fetching agent files:', error);
      } finally {
        setFilesLoading(false);
      }
    };

  // Fetch agent pages/files
  useEffect(() => {
    if (agentId && token) {
      fetchAgentFiles();
    }
  }, [agentId, API_URL, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm('Are you sure you want to save changes to this agent?')) return;
    
    setCheckingLimits(true);
    try {
      // If we're changing the agent name, check agent limits 
      // (this is considered a substantive change to the agent)
      if (agentName !== initialAgentName) {
        const limitResult = await checkResourceLimits(session, 'agent');
        setLimitCheck(limitResult);
        
        if (!limitResult.allowed) {
          // If over limit, don't proceed with update
          setCheckingLimits(false);
          return;
        }
        
        // Clear limit check since we're proceeding
        setLimitCheck(null); 
      }
      
      setLoading(true);
      // Build payload with allowed fields only
      const payload = {
        project_name: agentName,
        persona_instructions: agentPersona,
        agent_capability: agentCapability,
      };

      const response = await fetch(`${API_URL}/api/agent/${agentId}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        // Check if this is a limit exceeded error
        if (response.status === 402) {
          const errorData = await response.json();
          // Create a limit check object from the error response
          setLimitCheck({
            allowed: false,
            error: errorData.message,
            current: errorData.current,
            limit: errorData.limit,
            usagePercent: errorData.usagePercent,
            upgradeUrl: errorData.upgradeUrl
          });
          throw new Error(errorData.message || 'Agent limit exceeded');
        }
        
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update agent');
      }
      
      alert('Agent updated successfully!');
      // Refresh the page data to reflect changes
      window.location.reload();
    } catch (err) {
      console.error('Error updating agent:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
      setCheckingLimits(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/agent/projects/${agentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete agent');
      }
      alert('Agent deleted successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting agent:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) return;
    setFileDeleting(pageId);
    try {
      const response = await fetch(`${API_URL}/api/agent/${agentId}/pages/${pageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete file');
      }
      
      // Remove the deleted file from the state
      setAgentFiles(agentFiles.filter(file => file.id !== pageId));
      alert('File deleted successfully!');
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setFileDeleting(null);
    }
  };

  // Function to handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0]; // Get only the first file
    setUploadError('');
    
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB.`);
      return;
    }
    
    // Set selected files if all validations pass
    setSelectedFile(file);
    
    // Auto-check OCR if files are PDFs or images
    const needsOcr = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (needsOcr) {
      setIsOcrEnabled(true);
    }
  };

  // Function to handle file upload
  const handleFileUpload = async () => {
    if (!selectedFile) return;
    
    setCheckingLimits(true);
    setIsUploading(false);
    setUploadError('');
    
    try {
      // Check document limits before upload
      const limitResult = await checkResourceLimits(session, 'document');
      setLimitCheck(limitResult);
      
      if (!limitResult.allowed) {
        // Display error but don't proceed with upload
        setCheckingLimits(false);
        return;
      }
      
      // Continue with upload if limits allow
      setIsUploading(true);
      setLimitCheck(null); // Clear limit check since we're proceeding
      
      // Check if token exists
      if (!token) {
        throw new Error('You are not authenticated. Please log in again.');
      }
      
      const formData = new FormData();
      
      // Append each file to the FormData
      formData.append('file', selectedFile);
      
      // Add OCR flag as a string value to ensure proper parsing on server
      formData.append('is_ocr_enabled', isOcrEnabled.toString());
      
      // Ensure API_URL is valid
      if (!API_URL) {
        logger.error('API_URL is not defined');
        throw new Error('API configuration error. Please contact support.');
      }
      
      // Upload files with more detailed logging
      logger.debug(`Uploading file to ${API_URL}/api/agent/${agentId}/upload`);
      const response = await fetch(`${API_URL}/api/agent/${agentId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        // Check if this is a limit exceeded error
        if (response.status === 402) {
          const errorData = await response.json();
          // Create a limit check object from the error response
          setLimitCheck({
            allowed: false,
            error: errorData.message,
            current: errorData.current,
            limit: errorData.limit,
            usagePercent: errorData.usagePercent,
            upgradeUrl: errorData.upgradeUrl
          });
          throw new Error(errorData.message || 'Document limit exceeded');
        }
        
        // Try to parse error message from response
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || `Upload failed with status: ${response.status}`);
        } catch (jsonError) {
          // If JSON parsing fails, use the response status
          throw new Error(`Upload failed with status: ${response.status}`);
        }
      }
      
      // Clear form and refresh files list
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      alert('File uploaded successfully!');
      
      // Refresh files list
      fetchAgentFiles();
    } catch (error) {
      setUploadError(error.message || 'Failed to upload file');
      logger.error('File upload error:', error);
    } finally {
      setIsUploading(false);
      setCheckingLimits(false);
    }
  };

  // Function to remove a selected file before upload
  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Function to apply a template
  const applyTemplate = (templateKey) => {
    setAgentPersona(personaTemplates[templateKey]);
  };

  // Function to auto-resize the textarea
  const autoResizeTextarea = () => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';
      // Set the height to match the content
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  // Call autoResize when the content changes
  useEffect(() => {
    autoResizeTextarea();
  }, [agentPersona]);

  if (initialLoading) {
    return <div className="agent-loading">Loading agent data...</div>;
  }

  return (
    <div className="agent-page edit-agent-page">
      <div className="agent-header">
        <h1>Edit Agent: {agentName}</h1>
        <button type="button" className="delete-button" onClick={handleDelete} disabled={loading}>
          Delete Agent
        </button>
      </div>
      
      <div className="tabs-container">
        <div className="tabs-nav">
          <button 
            type="button" 
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Agent Settings
          </button>
          <button 
            type="button" 
            className={`tab-button ${activeTab === 'knowledge' ? 'active' : ''}`}
            onClick={() => setActiveTab('knowledge')}
          >
            Knowledge Base
          </button>
        </div>
        
        {/* Settings Tab Content */}
        <div className={`tab-content ${activeTab === 'settings' ? 'active' : ''}`}>
          <form onSubmit={handleSubmit} className="agent-form">
            <div className="form-section">
              <h3>Agent Configuration</h3>
              <div className="form-group">
                <label htmlFor="agentName">Agent Name*</label>
                <input
                  type="text"
                  id="agentName"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="Enter agent name"
                  required
                />
              </div>
              <div className="form-group persona-group">
                <label htmlFor="agentPersona">Agent Persona Guide</label>
                
                <div className="persona-templates">
                  <p className="template-label">Quick start with a template:</p>
                  <div className="template-buttons">
                    <button 
                      type="button" 
                      className="template-button"
                      onClick={() => applyTemplate('customer')}
                    >
                      Customer Support Expert
                    </button>
                    <button 
                      type="button" 
                      className="template-button"
                      onClick={() => applyTemplate('research')}
                    >
                      Research & Knowledge Assistant
                    </button>
                    <button 
                      type="button" 
                      className="template-button"
                      onClick={() => applyTemplate('creative')}
                    >
                      Creative Content Specialist
                    </button>
                    <button 
                      type="button" 
                      className="template-button"
                      onClick={() => applyTemplate('sales')}
                    >
                      Sales & Persuasion Expert
                    </button>
                    <button 
                      type="button" 
                      className="template-button"
                      onClick={() => applyTemplate('technical')}
                    >
                      Technical Support Specialist
                    </button>
                    <button 
                      type="button" 
                      className="template-button"
                      onClick={() => applyTemplate('education')}
                    >
                      Educational & Training Assistant
                    </button>
                    <button 
                      type="button" 
                      className="template-button"
                      onClick={() => applyTemplate('coaching')}
                    >
                      Personal & Business Coach
                    </button>
                    <button 
                      type="button" 
                      className="template-button"
                      onClick={() => applyTemplate('product')}
                    >
                      Product Specialist
                    </button>
                  </div>
                </div>
                
                <div className="helper-tip">
                  <span className="tip-icon">💡</span> 
                  <span className="tip-text">
                    These templates offer proven formats that significantly improve your agent's effectiveness.
                    Select one that matches your needs, then customize it for your specific use case.
                  </span>
                </div>
                
                <textarea
                  id="agentPersona"
                  ref={textareaRef}
                  value={agentPersona}
                  onChange={(e) => setAgentPersona(e.target.value)}
                  placeholder={getPersonaPlaceholder()}
                />
                
                <div className="field-description">
                  The more specific and detailed your instructions are, the better your agent will perform. Include clear examples of desired responses if possible.
                </div>
              </div>
              <div className="form-group">
                <label>Agent Capability</label>
                <div className="capability-options enhanced">
                  <button
                    type="button"
                    className={`capability-card ${agentCapability === 'optimal-choice' ? 'selected' : ''}`}
                    onClick={() => setAgentCapability('optimal-choice')}
                  >
                    <h4>Optimal Choice</h4>
                    <p>Balanced performance and quality for most use cases</p>
                  </button>
                  <button
                    type="button"
                    className={`capability-card ${agentCapability === 'fastest-responses' ? 'selected' : ''}`}
                    onClick={() => setAgentCapability('fastest-responses')}
                  >
                    <h4>Fastest Response</h4>
                    <p>Prioritizes speed over detailed answers</p>
                  </button>
                  <button
                    type="button"
                    className={`capability-card ${agentCapability === 'complex-tasks' ? 'selected' : ''}`}
                    onClick={() => setAgentCapability('complex-tasks')}
                  >
                    <h4>Complex Tasks</h4>
                    <p>Better for handling multi-step tasks</p>
                  </button>
                  <button
                    type="button"
                    className={`capability-card ${agentCapability === 'advanced-reasoning' ? 'selected' : ''}`}
                    onClick={() => setAgentCapability('advanced-reasoning')}
                  >
                    <h4>Advanced Reasoning</h4>
                    <p>Best for sophisticated analysis and deep insights</p>
                  </button>
                </div>
              </div>
            </div>

            <div className="form-actions">
              {limitCheck && <LimitWarning limitCheck={limitCheck} resourceType="agent" isError={!limitCheck.allowed} />}
              <button type="submit" className="save-button" disabled={loading || checkingLimits}>
                {loading ? 'Saving...' : (checkingLimits ? 'Checking Limits...' : 'Save Changes')}
              </button>
            </div>
          </form>
        </div>
        
        {/* Knowledge Base Tab Content */}
        <div className={`tab-content ${activeTab === 'knowledge' ? 'active' : ''}`}>
          <div className="agent-form">
            <div className="form-section">
              <h3>Agent Knowledge Base</h3>
              
              {/* File Upload Section */}
              <div className="upload-section">
                <h3>Upload New Files</h3>
                <div className="file-upload-area">
                  <div className="upload-container" onClick={() => fileInputRef.current?.click()}>
                    <div className="upload-icon">📁</div>
                    <div className="upload-text">
                      <p>Drag & drop a file here or <span className="browse-text">browse</span></p>
                      <p className="upload-limits">All file types accepted. Maximum size 100MB.</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </div>
                  
                  {/* Show selected file */}
                  {selectedFile && (
                    <div className="file-list">
                      <div className="file-item">
                        <div className="file-name">{selectedFile.name}</div>
                        <div className="file-size">{formatFileSize(selectedFile.size)}</div>
                        <button
                          type="button"
                          className="remove-file-btn"
                          onClick={removeSelectedFile}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* OCR Checkbox */}
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="ocrEnabled"
                      checked={isOcrEnabled}
                      onChange={(e) => setIsOcrEnabled(e.target.checked)}
                      className="checkbox-input"
                    />
                    <label htmlFor="ocrEnabled" className="checkbox-label">Enable OCR (Optical Character Recognition)</label>
                    <div className="field-description">
                      Enable this option for PDFs or images that contain text you want the agent to be able to read. Not applicable for other file types.
                    </div>
                  </div>
                  
                  {/* Upload button */}
                  <div className="upload-actions">
                    <button
                      type="button"
                      className="primary-button"
                      disabled={!selectedFile || isUploading || checkingLimits} 
                      onClick={handleFileUpload}
                    >
                      {isUploading ? 'Uploading...' : (checkingLimits ? 'Checking Limits...' : 'Upload File')}
                    </button>
                  </div>
                  
                  {/* Upload error */}
                  {uploadError && <div className="upload-error">{uploadError}</div>}
                </div>
              </div>
              
              {/* Limit Warning */}
              {limitCheck && <LimitWarning limitCheck={limitCheck} resourceType="document" isError={!limitCheck.allowed} />}
              
              <h3>Current Files</h3>
              {filesLoading ? (
                <div className="loading-files">Loading files...</div>
              ) : agentFiles.length === 0 ? (
                <div className="no-files">No files available</div>
              ) : (
                <div className="files-list">
                  <table className="files-table">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>File Size</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentFiles.map(file => (
                        <tr key={file.id}>
                          <td>{file.filename || 'Unnamed file'}</td>
                          <td>{formatFileSize(file.filesize)}</td>
                          <td>
                            <span className={`status-badge ${file.index_status}`}>
                              {file.index_status === 'ok' ? 'Indexed' : file.index_status}
                            </span>
                          </td>
                          <td>
                            <button 
                              type="button" 
                              className="delete-file-btn"
                              onClick={() => handleDeleteFile(file.id)}
                              disabled={fileDeleting === file.id}
                            >
                              {fileDeleting === file.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAgent;
